/**
 * Predictive search — live suggestions under every search field.
 *
 * Wires any form marked [data-predictive] (header bar + mobile drawer) to the
 * Predictive Search API, rendered through sections/predictive-search.liquid so the
 * panel markup, prices and copy stay in Liquid:
 *
 *   GET /search/suggest?q=…&resources[type]=query,product,article,page&section_id=predictive-search
 *
 * Typos are handled for free here — predictive search tolerates one wrong/extra letter
 * or two swapped ones, provided the first four are right. The results page does NOT
 * (different engine); assets/search-recover.js borrows this endpoint to cover it.
 *
 * Loaded as a standalone asset from sections/header.liquid, deliberately NOT as
 * {% javascript %}: section JS is concatenated into one bundle, where a fault in any
 * one script takes the rest of the header down with it (docs/GOTCHAS.md).
 *
 * The forms stay plain GET forms: with JS off, or before the first keystroke, Enter
 * still lands on /search. Nothing here is load-bearing.
 */
(function () {
  var MIN_CHARS = 2;
  var DEBOUNCE_MS = 200;
  var SECTION_ID = 'predictive-search';

  /**
   * Second-chance synonyms. Search & Discovery synonyms do NOT reach predictive search
   * (they only apply to the /search page), so the dropdown would otherwise be the one
   * place where "chiller" finds nothing. Used ONLY after a query returns zero results:
   * the term is rewritten once and asked again, under a "Showing results for X" note.
   *
   * Deliberately short. Source of truth for synonyms is the S&D app — this is the top of
   * the list mirrored by hand. Add a pair here only when it is also in S&D:
   * docs/plans/search-upgrade-2026-07.md.
   *
   * The dashboard keeps its own copy for catalogue reconciliation
   * (ops/catalog-additions/ui/search-synonyms.ts) — it deploys to Railway and cannot import from a
   * Shopify asset. Three places, one list: edit all three.
   */
  var ALIASES = {
    chiller: 'fridge',
    cooler: 'fridge',
    refrigerator: 'fridge',
    'deep freezer': 'freezer',
    draught: 'draft',
    barrel: 'keg',
    glasswasher: 'dishwasher',
    warewasher: 'dishwasher',
    'ice machine': 'ice maker',
    slushie: 'slush',
    granita: 'slush',
    'smoothie maker': 'blender',
    'coffee maker': 'coffee machine',
    'espresso maker': 'espresso machine',
    hood: 'extractor',
    canopy: 'extractor',
    'flat top': 'griddle',
    hotplate: 'griddle',
    'chip fryer': 'fryer',
    'food warmer': 'bain marie',
    glasses: 'glassware',
    stemware: 'glassware',
    'clay oven': 'tandoor',
    // Under-the-counter kit is the same thing under three regional names. Our titles
    // say "Undercounter" (prep table, ice makers) and "Underbar" (cocktail station),
    // so everything else points at those. Measured: underbench/lowboy return 0 here,
    // undercounter returns 3.
    underbench: 'undercounter',
    'under bench': 'undercounter',
    'under-counter': 'undercounter',
    lowboy: 'undercounter',
    'under-bar': 'underbar',
    'bar station': 'underbar'
  };

  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var seq = 0;

  var forms = document.querySelectorAll('[data-predictive]');
  if (!forms.length || !window.fetch || !window.DOMParser) return;
  Array.prototype.forEach.call(forms, setup);

  function setup(form) {
    var input = form.querySelector('input[name="q"]');
    var panel = form.querySelector('[data-predictive-panel]');
    var status = form.querySelector('[data-predictive-status]');
    if (!input || !panel) return;

    var panelId = 'pred-panel-' + ++seq;
    panel.id = panelId;
    input.setAttribute('role', 'combobox');
    input.setAttribute('aria-autocomplete', 'list');
    input.setAttribute('aria-expanded', 'false');
    input.setAttribute('aria-controls', panelId);
    input.setAttribute('autocomplete', 'off');

    var timer = null;
    var controller = null;
    var items = [];
    var active = -1;
    var served = ''; // term the panel currently shows

    input.addEventListener('input', onInput);
    input.addEventListener('focus', onFocus);
    input.addEventListener('keydown', onKeydown);
    document.addEventListener('click', onDocumentClick);

    function onInput() {
      var term = input.value.trim();
      if (timer) clearTimeout(timer);
      if (term.length < MIN_CHARS) {
        abort();
        served = '';
        close();
        return;
      }
      timer = setTimeout(function () { request(term, false); }, DEBOUNCE_MS);
    }

    function onFocus() {
      // Re-opening what was already fetched costs nothing; a changed term re-queries.
      var term = input.value.trim();
      if (term.length < MIN_CHARS) return;
      if (term === served && items.length) open();
      else onInput();
    }

    function onKeydown(event) {
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          if (panel.hidden) onFocus();
          else move(1);
          break;
        case 'ArrowUp':
          event.preventDefault();
          move(-1);
          break;
        case 'Enter':
          // No selection = the form submits as usual, straight to /search.
          if (active > -1 && items[active]) {
            event.preventDefault();
            window.location.href = items[active].href;
          }
          break;
        case 'Escape':
          if (!panel.hidden) {
            event.preventDefault();
            close();
          }
          break;
        case 'Tab':
          close();
          break;
      }
    }

    function onDocumentClick(event) {
      if (!form.contains(event.target)) close();
    }

    function abort() {
      if (controller) {
        controller.abort();
        controller = null;
      }
    }

    /**
     * @param {string} term  what to ask for
     * @param {boolean} viaAlias  true when `term` is a synonym we substituted, which both
     *   labels the panel and stops a second rewrite (no alias chains).
     */
    function request(term, viaAlias) {
      abort();
      controller = window.AbortController ? new AbortController() : null;

      if (panel.hidden) loading();

      var url = root + 'search/suggest?q=' + encodeURIComponent(term) +
        '&resources[type]=query,product,article,page' +
        '&resources[limit]=6&resources[limit_scope]=each' +
        '&section_id=' + SECTION_ID;

      fetch(url, controller ? { signal: controller.signal } : undefined)
        .then(function (res) {
          if (!res.ok) throw new Error('HTTP ' + res.status);
          return res.text();
        })
        .then(function (html) {
          var results = new DOMParser()
            .parseFromString(html, 'text/html')
            .querySelector('[data-predictive-results]');
          if (!results) throw new Error('No [data-predictive-results] in section response');

          if (!viaAlias && results.getAttribute('data-count') === '0') {
            var alias = aliasFor(term);
            if (alias) {
              request(alias, true);
              return;
            }
          }
          if (viaAlias) note(results, term);
          mount(results, term);
        })
        .catch(function (err) {
          if (err && err.name === 'AbortError') return;
          // Silent by design: the form still submits, so a failed suggestion is a
          // non-event for the shopper.
          if (window.console) console.error('Predictive search failed:', err);
          close();
        });
    }

    function loading() {
      var text = panel.getAttribute('data-loading-text') || '';
      panel.textContent = '';
      var p = document.createElement('p');
      p.className = 'pred-panel__loading';
      p.textContent = text;
      panel.appendChild(p);
      items = [];
      active = -1;
      open();
    }

    function note(results, term) {
      var template = panel.getAttribute('data-note-text');
      if (!template) return;
      var p = document.createElement('p');
      p.className = 'pred-results__note';
      p.setAttribute('role', 'presentation');
      p.textContent = template.replace('__TERM__', term);
      results.insertBefore(p, results.firstChild);
    }

    function mount(results, term) {
      // Rebuild the "See all results" href from the raw term. Liquid built it from
      // predictive_search.terms, which comes back HTML-escaped — url_encode on that
      // turns an ampersand into "%26amp%3B". The visible label is fine either way; only
      // the URL needs the untouched string, which only this side has.
      var all = results.querySelector('.pred-item--all');
      if (all) {
        all.href = form.getAttribute('action') + '?q=' + encodeURIComponent(term) +
          '&type=product,article,page&options[prefix]=last';
      }

      panel.textContent = '';
      panel.appendChild(results);
      served = input.value.trim();

      items = Array.prototype.slice.call(panel.querySelectorAll('[data-pred-item]'));
      // Option ids can only be assigned here: the same section HTML is mounted into two
      // panels on one page (header + drawer), so Liquid cannot make them unique.
      items.forEach(function (item, i) {
        item.id = panelId + '-opt-' + i;
        item.setAttribute('aria-selected', 'false');
      });
      active = -1;
      input.removeAttribute('aria-activedescendant');
      open();
      announce(results.getAttribute('data-resource-count') || '0');
    }

    function move(delta) {
      if (!items.length) return;
      if (active > -1) items[active].setAttribute('aria-selected', 'false');
      active += delta;
      if (active < 0) active = items.length - 1;
      if (active >= items.length) active = 0;
      var item = items[active];
      item.setAttribute('aria-selected', 'true');
      input.setAttribute('aria-activedescendant', item.id);
      if (item.scrollIntoView) item.scrollIntoView({ block: 'nearest' });
    }

    function announce(count) {
      if (!status) return;
      var template = panel.getAttribute('data-announce-text');
      status.textContent = template ? template.replace('__COUNT__', count) : '';
    }

    function open() {
      panel.hidden = false;
      input.setAttribute('aria-expanded', 'true');
    }

    function close() {
      if (panel.hidden) return;
      panel.hidden = true;
      input.setAttribute('aria-expanded', 'false');
      input.removeAttribute('aria-activedescendant');
      if (active > -1 && items[active]) items[active].setAttribute('aria-selected', 'false');
      active = -1;
    }
  }

  /**
   * Compound words typed without the space — "icemaker", "deepfryer", "bainmarie".
   * Measured on the live catalogue: every one of those returns zero, while the spaced
   * form returns results, so a split is worth trying.
   *
   * Only these known first halves split, and only when what follows is a word-sized
   * remainder. False splits are harmless anyway: this runs solely after a query already
   * came back empty, so a real term like "dishwasher" never reaches it.
   */
  var COMPOUND_HEADS = [
    'ice', 'coffee', 'deep', 'bain', 'glass', 'wine', 'beer', 'dish', 'food',
    'back', 'under', 'counter', 'hot', 'cold', 'soft', 'tea', 'juice', 'milk',
    // Bar-side compounds, measured the same way: backbar → 0 / back bar → 5,
    // speedrail → 0 / speed rail → 3, barfridge → 0 / bar fridge → 5.
    'speed', 'bar'
  ];

  /**
   * Whole term first, then the first word that has a synonym — so "beer chiller"
   * becomes "beer fridge" rather than losing the qualifier — and finally a compound split.
   * @returns {?string} rewritten term, or null when nothing matches.
   */
  function aliasFor(term) {
    var key = term.toLowerCase().trim();
    if (ALIASES[key]) return ALIASES[key];

    var words = key.split(/\s+/);
    for (var i = 0; i < words.length; i++) {
      if (ALIASES[words[i]]) {
        var rewritten = words.slice();
        rewritten[i] = ALIASES[words[i]];
        return rewritten.join(' ');
      }
    }

    if (words.length === 1) {
      for (var h = 0; h < COMPOUND_HEADS.length; h++) {
        var head = COMPOUND_HEADS[h];
        if (key.length > head.length + 2 && key.indexOf(head) === 0) {
          return head + ' ' + key.slice(head.length);
        }
      }
    }
    return null;
  }

  // Shared with assets/search-recover.js, which needs the same rewrite on the results
  // page. Header JS is earlier in the document than the search section, and deferred
  // scripts run in document order, so this is defined by the time that asset runs.
  window.BarEquipSearch = { aliasFor: aliasFor };
})();
